'use client'

import { fetchWithAuth } from '@/utils/api-error-handler'
import { formatPlanLabel as formatPlanLabelUtil } from '@/utils/plan-badge'
import { PERSONAL_WORKSPACE_ID } from '@/utils/workspace-scope'

export type TeamApiItem = {
  id: string
  name: string
  logo_url?: string | null
  permission_scope?: 'read' | 'write'
}

type TeamsListResponse = {
  data?: TeamApiItem[]
}

type BillingWorkspaceCurrentResponse = {
  items?: Array<{
    workspace_id: string
    workspace_type: string
    team_id: string | null
    plan_code: string
  }>
}

let teamsCache: TeamApiItem[] | null = null
let teamsInFlight: Promise<TeamApiItem[]> | null = null
const workspacePlanCache = new Map<string, string>()
let workspacePlansInFlight: Promise<void> | null = null

export function formatPlanLabel(planCode: string | null | undefined): string | null {
  const raw = String(planCode ?? '').trim()
  if (!raw) return null
  const label = formatPlanLabelUtil(raw)
  return label && label !== 'Unknown' ? label : null
}

export function getWorkspacePlanCode(workspaceKey: string): string | null {
  return workspacePlanCache.get(workspaceKey) ?? null
}

export async function fetchWorkspacePlans(apiUrl: string | null, forceRefresh: boolean = false): Promise<void> {
  if (!forceRefresh && workspacePlanCache.size > 0) return
  if (workspacePlansInFlight) return workspacePlansInFlight

  const requestUrl = `${apiUrl || ''}/v1/billing/workspaces/plans`
  workspacePlansInFlight = (async () => {
    const response = await fetchWithAuth(
      requestUrl,
      { method: 'GET', headers: { Accept: 'application/json' } },
      undefined,
      { skipUnauthorizedRedirect: true }
    )
    if (!response.ok) return
    const payload = (await response.json()) as BillingWorkspaceCurrentResponse
    const items = Array.isArray(payload.items) ? payload.items : []
    for (const item of items) {
      const code = String(item?.plan_code ?? '').trim()
      if (!code) continue
      const key = item.team_id ? String(item.team_id) : PERSONAL_WORKSPACE_ID
      workspacePlanCache.set(key, code)
    }
  })().finally(() => {
    workspacePlansInFlight = null
  })

  return workspacePlansInFlight
}

export async function fetchTeamsShared(url: string): Promise<TeamApiItem[]> {
  if (teamsCache) return teamsCache
  if (teamsInFlight) return teamsInFlight

  teamsInFlight = (async () => {
    const response = await fetchWithAuth(
      url,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
      undefined,
      { skipUnauthorizedRedirect: true }
    )
    if (!response.ok) return []
    const payload = (await response.json()) as TeamsListResponse
    const items = Array.isArray(payload?.data) ? payload.data : []
    teamsCache = items
    return items
  })().finally(() => {
    teamsInFlight = null
  })

  return teamsInFlight
}

export function upsertTeamInTeamsCache(team: TeamApiItem): void {
  teamsCache = teamsCache ? [team, ...teamsCache.filter((item) => item.id !== team.id)] : [team]
}

export function personalWorkspaceName(fullName: string | null | undefined, email: string | null | undefined): string {
  const nameSource = String(fullName || '').trim()
  const emailHandle = String(email || '')
    .trim()
    .split('@')[0]
  const firstToken = (nameSource || emailHandle || 'User').split(/\s+/)[0] || 'User'
  return firstToken.toLowerCase().endsWith('s') ? `${firstToken}' projects` : `${firstToken}'s projects`
}
